// src/lib/useWalletLogin.js
// Hook reutilizable del puente SIWE: Privy provee la wallet (embebida AA o externa)
// y NUESTRO backend (routes/wallet.js) emite la sesión. Lo usan tanto el botón
// simple (WalletLogin) como el onboarding guiado (WalletOnboarding).
//
// Flujo: start() → si no hay sesión Privy, abre su modal (crea wallet embebida con
// passkey/social o conecta externa) → al haber wallet, pide nonce, arma un mensaje
// HONESTO con el nonce, firma con personal_sign y verifica contra el backend →
// AuthContext.login(). status refleja cada etapa para que la UI la muestre.
import { useState, useEffect, useRef, useCallback } from 'react';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { useAuth } from '../Context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Mensaje de firma HONESTO: el usuario ve en humano qué autoriza. Incluye el nonce
// (el backend lo exige → anti-replay).
export function buildLoginMessage({ address, nonce }) {
  const domain = window.location.host;
  return [
    `Bitplify — Iniciar sesión`,
    ``,
    `Esto SOLO prueba que esta wallet es tuya.`,
    `NO autoriza mover, gastar ni tocar tus fondos.`,
    ``,
    `Sitio: ${domain}`,
    `Wallet: ${address}`,
    `Código de un solo uso: ${nonce}`,
  ].join('\n');
}

export function humanizeWalletError(err) {
  const m = (err && err.message) || String(err || '');
  if (/user rejected|rejected the request|denied|4001/i.test(m)) return 'Cancelaste la firma. Probá de nuevo cuando quieras.';
  if (/Failed to fetch/i.test(m)) return 'No se pudo conectar al servidor.';
  return m || 'No se pudo entrar con la wallet.';
}

// Preferí la wallet embebida de Privy (camino principal del plan); si no, la primera.
function pickWallet(wallets) {
  if (!wallets || !wallets.length) return null;
  return wallets.find(w => w.walletClientType === 'privy') || wallets[0];
}

export function useWalletLogin(onSuccess) {
  const { ready, authenticated, login: openPrivy } = usePrivy();
  const { wallets } = useWallets();
  const { login: authLogin } = useAuth();

  const [status, setStatus] = useState('idle'); // idle | connecting | signing | verifying | done | error
  const [error, setError]   = useState('');
  const pending = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const runSiwe = useCallback(async (wallet) => {
    try {
      const address = wallet.address.toLowerCase();

      setStatus('signing');
      const nres = await fetch(`${API_URL}/api/auth/wallet/nonce`);
      if (!nres.ok) throw new Error('No se pudo iniciar la firma.');
      const { nonce } = await nres.json();

      const message   = buildLoginMessage({ address, nonce });
      const provider  = await wallet.getEthereumProvider();
      const signature = await provider.request({ method: 'personal_sign', params: [message, address] });

      setStatus('verifying');
      const vres = await fetch(`${API_URL}/api/auth/wallet/verify`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ message, signature, address, nonce }),
      });
      const data = await vres.json().catch(() => ({}));
      if (!vres.ok || !data.token) throw new Error(data.message || 'No se pudo verificar la firma.');

      authLogin(data.user, data.token);
      setStatus('done');
      onSuccessRef.current?.(data.user);
    } catch (e) {
      setError(humanizeWalletError(e));
      setStatus('error');
    } finally {
      pending.current = false;
    }
  }, [authLogin]);

  // Cuando Privy queda autenticado y aparece una wallet, y hay un login pendiente
  // que disparamos nosotros, corremos el flujo SIWE.
  useEffect(() => {
    if (!pending.current || !ready || !authenticated) return;
    const wallet = pickWallet(wallets);
    if (!wallet) return;
    runSiwe(wallet);
  }, [ready, authenticated, wallets, runSiwe]);

  const start = useCallback(() => {
    setError('');
    setStatus('connecting');
    pending.current = true;
    const wallet = pickWallet(wallets);
    if (authenticated && wallet) runSiwe(wallet); // ya conectado → firmá directo
    else openPrivy();                              // abrí el modal de Privy
  }, [authenticated, wallets, openPrivy, runSiwe]);

  const reset = useCallback(() => { setStatus('idle'); setError(''); pending.current = false; }, []);

  const busy = status === 'connecting' || status === 'signing' || status === 'verifying';

  return { start, reset, status, error, ready, busy };
}
